import React, { useEffect, useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "../styles/pricing.module.css";
import DeconstructedCard from "../components/DeconstructedCard";
import LiquidPageShell from "../components/LiquidPageShell";
import { useAuth } from "../contexts/AuthContext";
import {
  redirectToPayment,
  getUserSubscription,
} from "../services/subscription";
import { toast } from "sonner";
import { trackInitiateCheckout } from "../utils/metaPixel";
import { trackTikTokInitiateCheckout } from "../utils/tiktokPixel";
import {
  ArrowLeft,
  Check,
  Users,
  Zap,
  Shield,
  Database,
  Clock,
  Book,
  Activity,
  MessageSquare,
  LogOut,
  CheckCircle,
  RefreshCw,
} from "react-feather";

const PRICING_PLANS = [
  {
    name: "Bramlo Student Plan",
    price: "$9.99",
    period: "per month",
    description:
      "Special student pricing. Everything you need to study smarter — unlimited AI, quizzes, flashcards, essays, and notes.",
    features: [
      "Unlimited AI conversations with Lumi",
      "Turn any PDF or notes into quizzes instantly",
      "Auto-generate essays & assignments",
      "Flashcard creation from any material",
      "Unlimited notes with rich formatting",
      "6 note templates (Lecture, Research, etc.)",
      "Auto-save & version history",
      "Smart search & organization",
      "Export everything (Markdown, Text, JSON)",
      "Priority support",
    ],
    highlighted: false,
    saveBadge: "SPECIAL STUDENT RATE",
    action: "Get Student Plan",
    planId: "premium_monthly",
    stripeLink: "https://buy.stripe.com/fZu7sMcmlaxb5CP1I1cQU08",
  },
  {
    name: "Bramlo Pro Annual",
    price: "$49.99",
    period: "per year",
    description:
      "All Pro features at ~$4.17/month. Best value for serious students — save 58% vs monthly.",
    features: [
      "Everything in Pro Monthly",
      "~$4.17/month (save 58% vs monthly)",
      "Unlimited AI conversations with Lumi",
      "All quiz, essay & flashcard tools",
      "Priority support",
      "All future features included",
    ],
    highlighted: true,
    saveBadge: "BEST VALUE",
    action: "Get Annual Plan",
    planId: "premium_annual",
    stripeLink: "https://buy.stripe.com/eVq9AU4TT34J1mzcmFcQU0f",
    annualNote: "~$4.17/mo \u00b7 billed annually at $49.99/year",
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "Contact Us",
    description:
      "For organizations that need admin controls and secure deployments.",
    features: [
      "All Premium features",
      "Teams & role management",
      "SSO/SAML and API access",
      "Centralized billing",
      "Compliance (GDPR/FERPA)",
      "Dedicated account manager",
    ],
    highlighted: false,
    action: "Contact Sales",
    planId: "enterprise",
  },
];

const FEATURES = [
  {
    title: "AI-Powered Learning",
    description:
      "Our advanced AI analyzes your learning patterns to create personalized quizzes and study materials that adapt to your needs.",
    icon: <Zap size={24} />,
  },
  {
    title: "Unlimited Access",
    description:
      "With our premium plans, create unlimited AI-generated quizzes, flashcards, and study materials to maximize your learning potential.",
    icon: <Database size={24} />,
  },
  {
    title: "Spaced Repetition",
    description:
      "Our scientifically-proven spaced repetition system ensures you review content at optimal intervals for maximum retention.",
    icon: <Clock size={24} />,
  },
  {
    title: "Advanced Analytics",
    description:
      "Track your progress with detailed analytics that show your strengths, weaknesses, and improvement over time.",
    icon: <Activity size={24} />,
  },
  {
    title: "Multi-Format Learning",
    description:
      "Convert text, audio, and images into interactive quizzes and flashcards to learn from any type of content.",
    icon: <Book size={24} />,
  },
  {
    title: "Priority Support",
    description:
      "Get fast, personalized support from our team of learning experts to help you make the most of our platform.",
    icon: <MessageSquare size={24} />,
  },
  {
    title: "Team Collaboration",
    description:
      "Share study materials and collaborate with classmates or colleagues to enhance group learning experiences.",
    icon: <Users size={24} />,
  },
  {
    title: "Enterprise Security",
    description:
      "Enterprise plans include advanced security features, SSO integration, and compliance with educational standards.",
    icon: <Shield size={24} />,
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, signOut } = useAuth();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPaymentComplete, setShowPaymentComplete] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [checkCount, setCheckCount] = useState(0);

  const handleIntersection = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(styles.inView);
        entry.target.observer?.unobserve(entry.target);
      }
    });
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      toast.error("Failed to sign out");
    }
  };

  const handleSelectPlan = async (planId, stripeLink) => {
    if (planId === "enterprise") {
      // For enterprise plans, redirect to contact page
      navigate("/contact", { state: { fromEnterprise: true } });
      return;
    }

    // REQUIRE AUTH FIRST: Users must create account before payment
    if (!isAuthenticated) {
      toast.info("Create your account to continue to checkout");
      navigate("/auth", {
        state: { planId, returnTo: `/pricing?plan=${planId}` },
      });
      return;
    }

    trackInitiateCheckout(planId);
    trackTikTokInitiateCheckout(planId);

    // For plans with Stripe links, redirect directly to Stripe
    if (stripeLink) {
      // Add user email as prefill to Stripe checkout
      const checkoutUrl = new URL(stripeLink);
      if (user?.email) {
        checkoutUrl.searchParams.append("prefilled_email", user.email);
      }
      setTimeout(() => {
        window.location.href = checkoutUrl.toString();
      }, 300);
      return;
    }

    if (isAuthenticated) {
      // If user is already logged in and no Stripe link, use old method
      const success = await redirectToPayment(planId);
      if (success) {
        setShowPaymentComplete(true);
        // Start checking for payment completion
        startPaymentCheck();
      }
    } else {
      // If user is not logged in, redirect to auth page with plan info
      navigate("/auth", { state: { planId } });
    }
  };

  // Function to check if payment is complete
  const checkPaymentStatus = async () => {
    if (!user || !isAuthenticated) return false;

    try {
      setIsCheckingPayment(true);
      // Get the latest subscription data
      const subscription = await getUserSubscription(user.id);

      // Check if subscription is active or trialing
      const isActive =
        subscription &&
        (subscription.status === "active" ||
          subscription.status === "trialing");

      // If subscription is active, redirect to success page
      if (isActive) {
        setCurrentSubscription(subscription);
        toast.success("Payment confirmed! Redirecting to your account...");
        setTimeout(() => {
          navigate("/dashboard/stripe-success");
        }, 1500);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    } finally {
      setIsCheckingPayment(false);
    }
  };

  // Start checking for payment completion
  const startPaymentCheck = () => {
    // Reset check count
    setCheckCount(0);

    // Check immediately
    checkPaymentStatus();

    // Set up interval to check every 5 seconds, up to 6 times (30 seconds total)
    const checkInterval = setInterval(async () => {
      setCheckCount((prev) => {
        const newCount = prev + 1;

        // If we've checked 6 times and still no success, stop checking
        if (newCount >= 6) {
          clearInterval(checkInterval);
        }

        return newCount;
      });

      const success = await checkPaymentStatus();

      // If payment is confirmed, stop checking
      if (success) {
        clearInterval(checkInterval);
      }
    }, 5000);

    // Clean up interval after 30 seconds if not cleared already
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 30000);
  };

  const handlePaymentComplete = () => {
    // Manually check payment status
    checkPaymentStatus();
  };

  // Fetch user's current subscription
  useEffect(() => {
    const fetchSubscription = async () => {
      if (isAuthenticated && user) {
        try {
          setIsLoading(true);
          const subscription = await getUserSubscription(user.id);
          setCurrentSubscription(subscription);
        } catch (error) {
          // Error fetching subscription
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [isAuthenticated, user]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
      rootMargin: "50px",
    });

    const elements = document.querySelectorAll("[data-animate]");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [handleIntersection]);

  // Check if user already has an active subscription
  const isSubscribed =
    currentSubscription &&
    (currentSubscription.status === "active" ||
      currentSubscription.status === "trialing");

  // Get the current plan name if subscribed
  const getCurrentPlanName = () => {
    if (!isSubscribed) return null;

    // Map the price ID to a plan name
    const priceId = currentSubscription.price_id;

    // Handle free tier (no price_id)
    if (!priceId || priceId === "free_tier_2024") return "Free";

    if (priceId.includes("basic")) return "Basic";
    if (priceId.includes("plus") && priceId.includes("annual"))
      return "Bramlo + Annual";
    if (priceId.includes("plus")) return "Bramlo +";

    return "Premium Plan";
  };

  // const currentPlanName = getCurrentPlanName(); // no longer needed since banner was removed

  return (
    <LiquidPageShell>
      <div className={styles.pricingContainer}>
        <div className={styles.content}>
          <Link to="/" className={styles.backButton}>
            <ArrowLeft size={18} />
            Back
          </Link>

          {isAuthenticated && (
            <button onClick={handleSignOut} className={styles.signOutButton}>
              Sign Out <LogOut size={18} />
            </button>
          )}

          <header className={styles.header}>
            <div className={styles.titleWrapper} data-animate>
              <h1 className={styles.title}>Simple, Transparent Pricing</h1>
            </div>
            <p className={styles.subtitle} data-animate>
              Upload assignments, create tests & quizzes, chat with your
              documents. Accelerate your studies with AI-powered learning.
            </p>

            {showPaymentComplete && (
              <div className={styles.paymentCompleteBanner} data-animate>
                <div className={styles.paymentCompleteContent}>
                  {isCheckingPayment ? (
                    <RefreshCw size={24} className={styles.spinningIcon} />
                  ) : (
                    <CheckCircle size={24} />
                  )}
                  <p>
                    {isCheckingPayment
                      ? "Checking payment status..."
                      : "Complete your payment in the new tab. We'll automatically detect when your payment is processed."}
                  </p>
                  {!isCheckingPayment && checkCount >= 6 && (
                    <button
                      className={styles.completePaymentButton}
                      onClick={handlePaymentComplete}
                    >
                      Check Payment Status
                    </button>
                  )}
                </div>
              </div>
            )}
          </header>

          <section className={styles.section}>
            <div className={styles.plansGrid}>
              {PRICING_PLANS.map((plan, index) => (
                <div
                  key={plan.name}
                  className={`${styles.planCard} ${
                    plan.highlighted ? styles.highlighted : ""
                  } ${styles.shellReset}`}
                  style={{ position: "relative" }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select ${plan.name} plan`}
                  onClick={() => handleSelectPlan(plan.planId, plan.stripeLink)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelectPlan(plan.planId, plan.stripeLink);
                    }
                  }}
                >
                  {(plan.highlighted || plan.saveBadge) && (
                    <div className={styles.saveBadge}>
                      {plan.saveBadge || "MOST POPULAR"}
                    </div>
                  )}
                  <DeconstructedCard theme={plan.highlighted ? "teal" : "navy"}>
                    <div className={styles.cardContent}>
                      <h3 className={styles.planName}>{plan.name}</h3>
                      <div className={styles.planPrice}>
                        <span className={styles.price}>{plan.price}</span>
                        {plan.period && (
                          <span className={styles.period}>{plan.period}</span>
                        )}
                      </div>
                      {plan.annualNote && (
                        <div className={styles.annualNote}>
                          {plan.annualNote}
                        </div>
                      )}
                      <ul className={styles.planFeatures}>
                        {plan.features.map((feature) => (
                          <li key={feature}>
                            <Check size={16} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        className={`${styles.planAction} ${styles.btnAnimateChars}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectPlan(plan.planId, plan.stripeLink);
                        }}
                        disabled={
                          isLoading ||
                          isCheckingPayment ||
                          (isSubscribed && getCurrentPlanName() === plan.name)
                        }
                      >
                        <span data-button-animate-chars>
                          {isSubscribed && getCurrentPlanName() === plan.name
                            ? "Current Plan"
                            : plan.action}
                        </span>
                      </button>
                    </div>
                  </DeconstructedCard>
                </div>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle} data-animate>
              Key Features
            </h2>
            <div className={styles.featuresGrid}>
              {FEATURES.map((feature, index) => (
                <div
                  key={feature.title}
                  className={styles.featureCard}
                  data-animate
                >
                  <div className={styles.featureIcon}>{feature.icon}</div>
                  <h3 className={styles.featureTitle}>{feature.title}</h3>
                  <p className={styles.featureDescription}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section
            className={`${styles.section} ${styles.universitiesSection}`}
          >
            <h2 className={styles.sectionTitle} data-animate>
              Used by Students At
            </h2>
            <div className={styles.universitiesCarouselContainer}>
              <div className={styles.universitiesCarousel}>
                {[
                  "Stanford",
                  "MIT",
                  "Harvard",
                  "Waterloo",
                  "Caltech",
                  "Berkeley",
                  "Princeton",
                  "Yale",
                  "Oxford",
                  "Cambridge",
                  "ETH Zürich",
                  "Imperial",
                  "Toronto",
                  "McGill",
                  "UBC",
                  "Carnegie Mellon",
                  "Columbia",
                  "UCLA",
                  "Georgia Tech",
                  "EPFL",
                ].map((uni, index) => (
                  <div
                    key={`${uni}-${index}`}
                    className={styles.universityItem}
                  >
                    <h3>{uni}</h3>
                  </div>
                ))}
                {[
                  "Stanford",
                  "MIT",
                  "Harvard",
                  "Waterloo",
                  "Caltech",
                  "Berkeley",
                  "Princeton",
                  "Yale",
                  "Oxford",
                  "Cambridge",
                  "ETH Zürich",
                  "Imperial",
                  "Toronto",
                  "McGill",
                  "UBC",
                  "Carnegie Mellon",
                  "Columbia",
                  "UCLA",
                  "Georgia Tech",
                  "EPFL",
                ].map((uni, index) => (
                  <div
                    key={`${uni}-${index}-duplicate`}
                    className={styles.universityItem}
                  >
                    <h3>{uni}</h3>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle} data-animate>
              Frequently Asked Questions
            </h2>
            <div className={styles.faqGrid}>
              {[
                {
                  title: "Can I switch between plans?",
                  description:
                    "Yes, you can upgrade or downgrade your plan at any time. Changes will take effect at the start of your next billing cycle.",
                },
                {
                  title: "Is there a free trial available?",
                  description:
                    "We offer limited access to basic features without a subscription. For full access, we recommend starting with the Student Plan, which you can cancel anytime.",
                },
                {
                  title: "How do I cancel my plan?",
                  description:
                    "You can cancel your subscription anytime from your account settings. Your access will continue until the end of your current billing period.",
                },
                {
                  title: "What payment methods do you accept?",
                  description:
                    "We accept all major credit cards, including Visa, Mastercard, American Express, and Discover. We also support Apple Pay and Google Pay.",
                },
                {
                  title: "Do you offer student discounts?",
                  description:
                    "Yes, we offer special discounts for students and educators. Contact our support team with your academic email to learn more about our educational pricing.",
                },
                {
                  title: "Can I share my account with others?",
                  description:
                    "Individual accounts are for personal use only. For team or classroom use, please check out our Enterprise plan, which offers multi-user access and team management features.",
                },
              ].map((faq, index) => (
                <div className={styles.faqCard} data-animate key={index}>
                  <h3 className={styles.faqQuestion}>{faq.title}</h3>
                  <p className={styles.faqAnswer}>{faq.description}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </LiquidPageShell>
  );
};

export default Pricing;
